import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      // Always show success to prevent email enumeration
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 p-8 text-center border-b border-zinc-800">
            <div className="w-14 h-14 rounded-xl bg-[#02b5ff]/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-7 w-7 text-[#02b5ff]" />
            </div>
            <h1 className="text-xl font-bold text-white">Reset Your Password</h1>
            <p className="text-sm text-zinc-400 mt-2">
              {sent ? "Check your inbox" : "Enter your email and we'll send you a reset link"}
            </p>
          </div>

          <div className="p-8">
            {sent ? (
              <div className="text-center space-y-4" data-testid="reset-email-sent">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
                <p className="text-sm text-zinc-300">
                  If an account exists with <span className="text-white font-medium">{email}</span>, 
                  you'll receive a password reset link shortly.
                </p>
                <p className="text-xs text-zinc-500">
                  Check your spam folder if you don't see it within a few minutes.
                </p>
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 text-sm text-[#02b5ff] hover:underline mt-4"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
                    placeholder="Enter your email"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white h-11"
                  data-testid="send-reset-link-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Link 
                  to="/login" 
                  className="flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
